import { BoxProps } from '@chakra-ui/react';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import {
  ProductGalleryWithThumbnails,
  ProductGalleryWithThumbnailsProps,
  Slide,
} from '@/shared/ui';
import { prepareColor } from '../../lib/colorUtils';

type ProductVariant = {
  color: {
    value: string;
    label: string;
    hex: string;
  };
  images: Slide[];
};

interface ProductImageGalleryProps
  extends Omit<ProductGalleryWithThumbnailsProps, 'slides' | 'activeImage'>,
    BoxProps {
  variants: ProductVariant[];
  commonImages: Slide[];
}

const ProductImageGallery = ({ variants, commonImages, ...props }: ProductImageGalleryProps) => {
  const searchParams = useSearchParams();
  const selectedColor = prepareColor(searchParams?.get('color'));
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const allImages = useMemo(() => {
    const variantImages = variants.flatMap((variant) => variant.images);
    const combined = [...variantImages, ...commonImages];

    const uniqueImages = combined.filter(
      (img, index, self) => index === self.findIndex((t) => t.id === img.id || t.src === img.src),
    );

    return uniqueImages;
  }, [variants, commonImages]);

  useEffect(() => {
    if (!selectedColor) return;

    const colorVariant = variants.find((v) => v.color.value === selectedColor);
    if (!colorVariant?.images.length) return;

    const firstColorImageIndex = allImages.findIndex((img) =>
      colorVariant.images.some((variantImg) => variantImg.id === img.id),
    );

    if (firstColorImageIndex !== -1) {
      setActiveImageIndex(firstColorImageIndex);
    }
  }, [selectedColor, allImages, variants]);

  if (allImages.length === 0) {
    console.error('ProductImageGallery: No images available to display');
    return null;
  }

  return (
    <ProductGalleryWithThumbnails
      {...props}
      activeImage={activeImageIndex}
      slides={allImages}
    />
  );
};

export default ProductImageGallery;
